import { useContext, useEffect, useState } from 'react'

import {
  Autocomplete,
  Box,
  Button,
  Icon,
  Input,
  Pagination,
  Table,
  TableHeader,
  Text,
} from '@fairfashion/design-system'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Card } from '../../components/Card/card.comp'
import { env } from '../../configs/env.config'
import { IBrandsResponseDTO } from '../../sdk/brand/brand.types'
import { IItemWithFinishedItemProfile } from '../../sdk/finished-item-profile/finished-item-profile.types'
import { sdk } from '../../services/sdk'

import { QRCodeContext, QRCodeProvider } from './qrcode.context'
import * as Styles from './qrcode.styles'
import { isQRCodeFilled } from './qrcode.utils'

const QRCodeComp = () => {
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedBrand, setSelectedBrand] = useState<IBrandsResponseDTO>()
  const [finishedItems, setFinishedItems] = useState<
    IItemWithFinishedItemProfile[]
  >([])

  const { brands, statistics, actions } = useContext(QRCodeContext)

  const theme = useTheme()

  const handleSearch = async () => {
    const { data, pages } =
      await sdk.finishedItemProfile.getFinishedItemProfiles(
        page,
        search,
        selectedBrand?.id,
      )

    setTotalPages(pages)
    setFinishedItems(data)
  }

  useEffect(() => {
    actions.getBrands()
    actions.getStatistics()
  }, [])

  useEffect(() => {
    handleSearch()
  }, [page])

  const navigate = useNavigate()

  const headers: TableHeader[] = [
    {
      key: 'code',
      label: 'Código SKU',
      width: '10%',
      minWidth: '150px',
    },
    {
      key: 'finishedItemProfile.name',
      label: 'Nome',
      minWidth: '150px',
    },
    {
      key: 'finishedItemProfile.description',
      label: 'Descrição',
      minWidth: '150px',
      customRender: (data) => {
        return (
          <Box width="100%">
            <Styles.DescriptionText size="small">
              {data?.finishedItemProfile?.description}
            </Styles.DescriptionText>
          </Box>
        )
      },
    },
    {
      key: 'brand.name',
      label: 'Marca',
      width: '100px',
      align: 'center',
    },
    {
      key: 'finishedItemStatistics.accessCount',
      label: 'Acessos',
      align: 'center',
      width: '80px',
      customRender: (data) => {
        return (
          <Box justifyContent="center" width="100%">
            <Text size="small">
              {data?.finishedItemStatistics?.accessCount ?? 0}
            </Text>
          </Box>
        )
      },
    },
    {
      key: 'qrcode',
      label: 'QR Code',
      align: 'center',
      width: '80px',
      customRender: (data) => {
        return (
          <Box justifyContent="center" width="100%">
            <a href={env.QRCODE_URL + data.code} target="_blank">
              <Box direction="row" gap={1} alignItems="center">
                <Icon name="qr_code" color="info" size={20} />
                <Text size="small" color="info">
                  Link
                </Text>
              </Box>
            </a>
          </Box>
        )
      },
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      width: '80px',
      customRender: (data) => {
        return isQRCodeFilled(data) ? (
          <Box justifyContent="center" width="100%">
            <Styles.StatusContainer completed>Completo</Styles.StatusContainer>
          </Box>
        ) : (
          <Box justifyContent="center" width="100%">
            <Styles.StatusContainer>Incompleto</Styles.StatusContainer>
          </Box>
        )
      },
    },
    {
      key: 'qrcode_access',
      label: 'Ativo',
      align: 'center',
      width: '40px',
      customRender: (data) => {
        return data.finishedItemProfile?.qrcodeAccess ? (
          <Box justifyContent="center" width="100%">
            <Icon name="check" color="success" size={20} />
          </Box>
        ) : (
          <Box justifyContent="center" width="100%">
            <Icon name="close" color="error" size={20} />
          </Box>
        )
      },
    },
    {
      key: 'edit',
      label: 'Ver',
      align: 'center',
      width: '60px',
      customRender: (data) => {
        return (
          <Box justifyContent="center" width="100%">
            <Button
              variant="text"
              padding="8px"
              onClick={() => navigate(`/qrcode/${data.code}`)}
            >
              <Icon name="menu_search" color="secondary" size={20} />
            </Button>
          </Box>
        )
      },
    },
  ]

  return (
    <Box direction="column" width="100%" gap={2} padding={3}>
      <Styles.CardContainer
        width="100%"
        boxShadow={theme.box_shadow.card}
        borderRadius={theme.border_radius.card}
        flexWrap="wrap"
      >
        <Styles.ImageBackground src="/assets/card-background.jpeg" />
        <Styles.CardContentContainer
          gap={2}
          padding={3}
          width="100%"
          flexWrap="wrap"
        >
          <Card
            title="Quantidade de SKUs"
            value={`${statistics?.skusCount ?? 0}`}
            hideIcon
          />
          <Card
            title="Total de acessos aos QRCodes"
            value={`${statistics?.totalAccessCount ?? 0}`}
            hideIcon
          />
          <Card
            title="SKUs rastreados até o final"
            value={`${statistics?.finishedItemsTracedToEndSupplierCount ?? 0}`}
            hideIcon
          />
          <Card
            title="SKUs com ordem de produção"
            value={`${statistics?.finishedItemsWithOrderProductionCount ?? 0}`}
            hideIcon
          />
        </Styles.CardContentContainer>
      </Styles.CardContainer>

      <Box
        flex={1}
        width="100%"
        backgroundColor="white"
        direction="column"
        gap={3}
        borderRadius={theme.border_radius.box}
        padding={3}
        border={{
          color: 'grey_5',
          width: 1,
        }}
        boxShadow={theme.box_shadow.card}
        marginTop={3}
      >
        <Box
          width="100%"
          gap={2}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <Box maxWidth="400px" minWidth="140px" flex={1}>
            <Input
              placeholder="Pesquisa"
              adornmentPosition="left"
              adornment={<Icon name="search" size={20} color="grey_2" />}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box gap={2} flex={1}>
            <Box maxWidth="250px" minWidth="140px" flex={1}>
              <Autocomplete
                placeholder="Marca"
                options={[{ name: 'Todas', id: 'all' }, ...brands]}
                getOptionLabel={(brand) => (brand as IBrandsResponseDTO).name}
                onChange={(selected) =>
                  setSelectedBrand(selected as IBrandsResponseDTO)
                }
              />
            </Box>
            <Button width="100px" onClick={() => handleSearch()}>
              Pesquisar
            </Button>
          </Box>
        </Box>
        <Table
          headers={headers}
          data={finishedItems}
          footer={
            <Box justifyContent="center" width="100%" paddingY={1}>
              <Pagination
                total={totalPages}
                current={page}
                onChange={(_page) => setPage(_page)}
              />
            </Box>
          }
        />
      </Box>
    </Box>
  )
}

export const QRCode = () => {
  return (
    <QRCodeProvider>
      <QRCodeComp />
    </QRCodeProvider>
  )
}
